import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Icon,
  useTheme,
  alpha,
  styled,
  Chip,
  Stack,
  Skeleton,
} from "@mui/material";
import { SessionResponse } from "../../../types/pos.types";
import dayjs from "dayjs";
import { posService } from "../../../../../services/pos.service";
import { toast } from "react-toastify";

interface CashReconciliationProps {
  denominationData: {
    bills: Array<{ value: number; count: number }>;
    coins: Array<{ value: number; count: number }>;
  };
  sessionData: SessionResponse;
  onNext: () => void;
}

// Custom colors to match the system theme
const THEME_COLORS = {
  primary: "#1B7B6B", // Deep teal from header
  primaryLight: "#1E8078", // Lighter teal for hover states
  primaryDark: "#166F68", // Darker teal for active states
  success: "#059669",
  danger: "#B91C1C", // Deeper, more sophisticated red that complements teal
  background: "#FFFFFF",
  text: {
    primary: "#1A202C",
    secondary: "#4A5568",
    muted: "#718096",
  },
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: alpha(theme.palette.background.default, 0.5),
  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
  borderRadius: theme.shape.borderRadius,
  height: "100%",
}));

const DetailBox: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => {
  const theme = useTheme();
  return (
    <StyledPaper>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
        {value}
      </Typography>
    </StyledPaper>
  );
};

const LoadingSkeleton = () => (
  <Box>
    <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
      {[1, 2, 3, 4].map((i) => (
        <Box sx={{ flex: 1 }} key={i}>
          <StyledPaper>
            <Skeleton variant="text" width="60%" height={14} />
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}
            >
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width="40%" height={20} />
            </Box>
          </StyledPaper>
        </Box>
      ))}
    </Stack>

    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <StyledPaper sx={{ p: 3, minHeight: 220 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1.5,
              pb: 1.5,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width="40%" height={32} />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="text" width="40%" height={32} />
            </Grid>
          </Grid>
          <Box sx={{ mt: "auto", pt: 2, borderTop: 1, borderColor: "divider" }}>
            <Skeleton variant="text" width="30%" height={20} />
            <Skeleton
              variant="rectangular"
              width="60%"
              height={48}
              sx={{ mt: 1, borderRadius: 1 }}
            />
          </Box>
        </StyledPaper>
      </Grid>
      <Grid item xs={12} md={6}>
        <StyledPaper sx={{ p: 3, minHeight: 220 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1.5,
              pb: 1.5,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width="40%" height={32} />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="text" width="40%" height={32} />
            </Grid>
          </Grid>
          <Box sx={{ mt: "auto", pt: 2, borderTop: 1, borderColor: "divider" }}>
            <Skeleton variant="text" width="30%" height={20} />
            <Skeleton
              variant="rectangular"
              width="60%"
              height={48}
              sx={{ mt: 1, borderRadius: 1 }}
            />
          </Box>
        </StyledPaper>
      </Grid>
    </Grid>
  </Box>
);

export const CashReconciliation: React.FC<CashReconciliationProps> = ({
  denominationData,
  sessionData,
  onNext,
}) => {
  const theme = useTheme();

  const [totalSalesLoading, setTotalSalesLoading] = React.useState(false);
  const [totalSales, setTotalSales] = React.useState(0);

  console.log({ sessionData });
  console.log({ denominationData });

  const calculateTotal = (items: Array<{ value: number; count: number }>) => {
    return items.reduce((sum, item) => sum + item.value * item.count, 0);
  };

  const initialTotal =
    calculateTotal(sessionData.denominations.initial.bills) +
    calculateTotal(sessionData.denominations.initial.coins);
  const finalTotal =
    calculateTotal(denominationData.bills) +
    calculateTotal(denominationData.coins);
  const salesTotal = totalSales;
  const expectedTotal = initialTotal + salesTotal;
  const difference = finalTotal - expectedTotal;

  const getDuration = () => {
    const start = dayjs(sessionData.timestamps.start);
    const end = sessionData.timestamps.end
      ? dayjs(sessionData.timestamps.end)
      : dayjs();
    return `${end.diff(start, "hour")}h ${end.diff(start, "minute") % 60}m`;
  };

  const getTotalSales = () => {
    setTotalSalesLoading(true);
    posService
      .getSessionTotalSales({
        session_id: sessionData.id,
      })
      .then((res) => {
        const total = res.data;
        setTotalSales(total);
      })
      .catch(() => {
        toast.error(
          "Error happened while trying to fetch total sales of this session"
        );
      })
      .finally(() => {
        setTotalSalesLoading(false);
      });
  };

  useEffect(getTotalSales, [sessionData]);

  if (totalSalesLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <Box>
      {/* Cash Denominations Summary */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <DetailBox
            label="Initial Bills Count"
            value={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Icon sx={{ fontSize: 16, color: THEME_COLORS.primary }}>
                  payments
                </Icon>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: THEME_COLORS.primary }}
                >
                  {sessionData.denominations.initial.bills.reduce(
                    (sum: number, bill: { count: number }) => sum + bill.count,
                    0
                  )}{" "}
                  Notes
                </Typography>
              </Box>
            }
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <DetailBox
            label="Initial Coins Count"
            value={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Icon sx={{ fontSize: 16, color: THEME_COLORS.primary }}>
                  paid
                </Icon>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: THEME_COLORS.primary }}
                >
                  {sessionData.denominations.initial.coins.reduce(
                    (sum: number, coin: { count: number }) => sum + coin.count,
                    0
                  )}{" "}
                  Coins
                </Typography>
              </Box>
            }
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <DetailBox
            label="Final Bills Count"
            value={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Icon sx={{ fontSize: 16, color: THEME_COLORS.primary }}>
                  payments
                </Icon>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: THEME_COLORS.primary }}
                >
                  {denominationData.bills.reduce(
                    (sum: number, bill: { count: number }) => sum + bill.count,
                    0
                  )}{" "}
                  Notes
                </Typography>
              </Box>
            }
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <DetailBox
            label="Final Coins Count"
            value={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Icon sx={{ fontSize: 16, color: THEME_COLORS.primary }}>
                  paid
                </Icon>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: THEME_COLORS.primary }}
                >
                  {denominationData.coins.reduce(
                    (sum: number, coin: { count: number }) => sum + coin.count,
                    0
                  )}{" "}
                  Coins
                </Typography>
              </Box>
            }
          />
        </Box>
      </Stack>

      <Grid container spacing={2}>
        {/* Cash Flow Section */}
        <Grid item xs={12} md={6}>
          <StyledPaper sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: THEME_COLORS.primary,
                display: "flex",
                alignItems: "center",
                gap: 1,
                pb: 1.5,
                borderBottom: `1px solid ${alpha(THEME_COLORS.primary, 0.2)}`,
              }}
            >
              <Icon>account_balance_wallet</Icon>
              Cash Flow
            </Typography>

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Initial Cash
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ color: THEME_COLORS.primary, fontWeight: 600 }}
                >
                  BD {initialTotal.toFixed(3)}
                </Typography>
              </Grid>
            </Grid>

            <Box
              sx={{
                mt: "auto",
                pt: 2,
                borderTop: `1px solid ${alpha(THEME_COLORS.primary, 0.2)}`,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                Total Sales
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color:
                    salesTotal === 0
                      ? THEME_COLORS.text.muted
                      : salesTotal > 0
                      ? THEME_COLORS.primary
                      : THEME_COLORS.danger,
                  lineHeight: 1.2,
                }}
              >
                BD {salesTotal.toFixed(3)}
              </Typography>
            </Box>
          </StyledPaper>
        </Grid>

        {/* Reconciliation Section */}
        <Grid item xs={12} md={6}>
          <StyledPaper sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: THEME_COLORS.primary,
                display: "flex",
                alignItems: "center",
                gap: 1,
                pb: 1.5,
                borderBottom: `1px solid ${alpha(THEME_COLORS.primary, 0.2)}`,
              }}
            >
              <Icon>balance</Icon>
              Reconciliation
            </Typography>

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Final Cash Count
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ color: THEME_COLORS.primary, fontWeight: 600 }}
                >
                  BD {finalTotal.toFixed(3)}
                </Typography>
              </Grid>
            </Grid>

            <Box
              sx={{
                mt: "auto",
                pt: 2,
                borderTop: `1px solid ${alpha(THEME_COLORS.primary, 0.2)}`,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                Difference
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color:
                    difference === 0
                      ? THEME_COLORS.text.muted
                      : difference > 0
                      ? THEME_COLORS.primary
                      : THEME_COLORS.danger,
                  lineHeight: 1.2,
                }}
              >
                BD {difference.toFixed(3)}
              </Typography>
            </Box>
          </StyledPaper>
        </Grid>
      </Grid>
    </Box>
  );
};
